/**
 * /complaints — 投诉问题矩阵 + 清单（Phase 4 §4.4）。
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Drawer } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import ChipFilter from '@/components/ChipFilter';
import DataTable from '@/components/DataTable';
import HeatmapMatrix from '@/components/HeatmapMatrix';
import MockBanner from '@/components/MockBanner';
import Panel from '@/components/Panel';
import { api } from '@/lib/api';
import { useWeekStore } from '@/stores/weekStore';

interface ComplaintRow {
  complaint_id: string;
  source: string;
  brand_id: string;
  model_id: string;
  complaint_date: string;
  title: string | null;
  content: string | null;
  issue_category_l1: string | null;
  severity: string | null;
  is_safety_related: boolean;
  is_battery_related: boolean;
  is_software_related: boolean;
  response_status: string | null;
  source_url: string;
}

interface ModelRow {
  model_id: string;
  brand_id: string;
  model_name: string;
}

// PR 9.2b-1 C2: 10 桶 (拆 电池/三电 → 三电系统 + 续航与充电)
// 跟 packages/shared/python/gac_shared/enums.py IssueCategoryL1 严格对齐
// (set equality 由 apps/web/src/lib/__tests__/l1_taxonomy.test.ts 校验)
const L1_NAMES = [
  '动力系统',
  '三电系统',
  '续航与充电',
  '制动/转向',
  '智能座舱',
  '智驾/辅助驾驶',
  '车身/底盘',
  '内饰/配置',
  '售后服务',
  '销售争议',
];

const BRAND_OPTIONS = [
  { value: '', label: '全部品牌' },
  { value: 'GAC-AION', label: '广汽埃安' },
  { value: 'GAC-TRUMP', label: '广汽传祺' },
  { value: 'GAC-HYPER', label: '昊铂' },
  { value: 'GAC-HONDA', label: '广汽本田' },
  { value: 'GAC-TOYOTA', label: '广汽丰田' },
];

const SEVERITY_OPTIONS = [
  { value: '', label: '全部严重性' },
  { value: '低', label: '低' },
  { value: '中', label: '中' },
  { value: '高', label: '高' },
  { value: '重大', label: '重大' },
];

const SAFETY_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'true', label: '仅安全相关' },
  { value: 'false', label: '非安全相关' },
];

export default function ComplaintsPage() {
  const { week } = useWeekStore();
  const [brand, setBrand] = useState('');
  const [severity, setSeverity] = useState('');
  const [safety, setSafety] = useState('');
  const [drawerRow, setDrawerRow] = useState<ComplaintRow | null>(null);

  const complaintsQ = useQuery({
    queryKey: ['complaints', week, brand, severity, safety],
    queryFn: async () => {
      const params: Record<string, string> = { week, limit: '200' };
      if (brand) params.brand = brand;
      if (severity) params.severity = severity;
      if (safety) params.is_safety_related = safety;
      return (await api.get<ComplaintRow[]>('/complaints', { params })).data;
    },
    retry: false,
  });

  const modelsQ = useQuery({
    queryKey: ['allModels'],
    queryFn: async () => (await api.get<ModelRow[]>('/models')).data,
    retry: false,
  });

  const complaints = useMemo(() => complaintsQ.data ?? [], [complaintsQ.data]);
  const models = useMemo(() => modelsQ.data ?? [], [modelsQ.data]);
  const usingMock = !complaintsQ.data && !complaintsQ.isLoading;

  // Build heatmap data: model_id × L1
  const heatmap = useMemo(() => {
    const yLabels = models.map((m) => m.model_name);
    const counter = new Map<string, number>();
    for (const c of complaints) {
      if (!c.issue_category_l1) continue;
      const key = `${c.model_id}|${c.issue_category_l1}`;
      counter.set(key, (counter.get(key) ?? 0) + 1);
    }
    const data: Array<[number, number, number]> = [];
    models.forEach((m, yi) => {
      L1_NAMES.forEach((l1, xi) => {
        const v = counter.get(`${m.model_id}|${l1}`) ?? 0;
        if (v > 0) data.push([yi, xi, v]);
      });
    });
    return { yLabels, xLabels: L1_NAMES, data };
  }, [models, complaints]);

  const columns: ColumnsType<ComplaintRow> = [
    {
      title: '日期',
      dataIndex: 'complaint_date',
      width: 110,
      render: (v) => <span className="text-[12px] text-slate-500">{v}</span>,
    },
    { title: '来源', dataIndex: 'source', width: 110 },
    { title: '品牌', dataIndex: 'brand_id', width: 110 },
    { title: '车型', dataIndex: 'model_id', width: 130 },
    {
      title: '标题',
      dataIndex: 'title',
      ellipsis: true,
      render: (v) => v ?? '(无标题)',
    },
    { title: '一级类目', dataIndex: 'issue_category_l1', width: 110 },
    { title: '严重性', dataIndex: 'severity', width: 80 },
    {
      title: '安全',
      dataIndex: 'is_safety_related',
      width: 80,
      render: (v: boolean) => (v ? <span className="text-risk-major">是</span> : '否'),
    },
    { title: '状态', dataIndex: 'response_status', width: 110, render: (v) => v ?? '—' },
  ];

  return (
    <div className="space-y-5">
      {usingMock && <MockBanner message="投诉接口暂不可用 — 显示空清单" />}

      <div>
        <h1 className="text-[17px] lg:text-[20px] font-semibold text-slate-900 m-0">投诉问题矩阵 · {week}</h1>
        <p className="text-[12px] lg:text-[13px] text-slate-500 mt-1 mb-0">
          {complaints.length} 条投诉 · 车型 × 一级问题热力矩阵
        </p>
      </div>

      <Panel title="筛选">
        <div className="flex flex-col gap-3">
          <ChipFilter label="品牌" value={brand} onChange={setBrand} options={BRAND_OPTIONS} />
          <ChipFilter label="严重性" value={severity} onChange={setSeverity} options={SEVERITY_OPTIONS} />
          <ChipFilter label="安全相关" value={safety} onChange={setSafety} options={SAFETY_OPTIONS} />
        </div>
      </Panel>

      <Panel title="车型 × 一级问题热力矩阵">
        {heatmap.yLabels.length > 0 ? (
          // 9.2f: mobile 横滚容器,矩阵保最小宽度防 10 列挤压
          <div className="overflow-x-auto">
            <div className="min-w-[640px] lg:min-w-0">
              <HeatmapMatrix
                yLabels={heatmap.yLabels}
                xLabels={heatmap.xLabels}
                data={heatmap.data}
                height={Math.max(360, heatmap.yLabels.length * 14 + 120)}
              />
            </div>
          </div>
        ) : (
          <div className="text-[12px] text-slate-400 py-12 text-center">暂无投诉数据</div>
        )}
      </Panel>

      <Panel title="投诉清单" noPadding>
        <div className="p-4">
          <DataTable<ComplaintRow>
            columns={columns}
            data={complaints}
            rowKey="complaint_id"
            loading={complaintsQ.isLoading}
            pageSize={20}
            onRowClick={(row) => setDrawerRow(row)}
          />
        </div>
      </Panel>

      <Drawer
        open={drawerRow !== null}
        onClose={() => setDrawerRow(null)}
        width="min(92vw, 560px)"
        title="投诉详情"
      >
        {drawerRow && (
          <div className="space-y-3 text-[13px]">
            <div>
              <span className="text-slate-500">投诉 ID：</span>
              <span className="text-slate-900">{drawerRow.complaint_id}</span>
            </div>
            <div>
              <span className="text-slate-500">来源：</span>
              <span className="text-slate-900">{drawerRow.source}</span>
            </div>
            <div>
              <span className="text-slate-500">品牌车型：</span>
              <span className="text-slate-900">
                {drawerRow.brand_id} · {drawerRow.model_id}
              </span>
            </div>
            <div>
              <span className="text-slate-500">日期：</span>
              <span className="text-slate-900">{drawerRow.complaint_date}</span>
            </div>
            <div>
              <span className="text-slate-500">分类：</span>
              <span className="text-slate-900">
                {drawerRow.issue_category_l1 ?? '—'} · {drawerRow.severity ?? '—'}
              </span>
            </div>
            <div>
              <span className="text-slate-500">标记：</span>
              <span className="text-slate-900">
                {drawerRow.is_safety_related && '安全 '}
                {drawerRow.is_battery_related && '电池 '}
                {drawerRow.is_software_related && '软件'}
              </span>
            </div>
            <div>
              <div className="text-slate-500 mb-1">原文：</div>
              <div className="bg-slate-50 p-3 rounded leading-relaxed text-slate-700">
                <strong>{drawerRow.title}</strong>
                <p className="mt-2 mb-0 whitespace-pre-wrap">{drawerRow.content ?? '(无内容)'}</p>
              </div>
            </div>
            <div>
              <a href={drawerRow.source_url} target="_blank" rel="noreferrer" className="text-sky-600 hover:underline">
                查看原文 ↗
              </a>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
